/**
 * Pro signup OTP failure tracking.
 *
 * Recording failures goes through Core API (service-role RPC) so anon
 * clients cannot DoS-ban emails. Clearing runs as authenticated RPC
 * after a successful verify. get_pro_otp_attempt_state stays a read-only
 * RPC (fail closed on error).
 */
import { getApiBase } from './apiBase';
import { supabase } from './supabase';

export type ProOtpAttemptState = {
  failed_attempts: number;
  blocked: boolean;
  remaining: number;
  already_verified?: boolean;
  ok?: boolean;
  error?: string;
  /** True when the RPC/API call itself failed — treat as fail-closed. */
  unavailable?: boolean;
};

function asState(raw: unknown): ProOtpAttemptState {
  const o = (raw ?? {}) as Record<string, unknown>;
  const failed = Number(o.failed_attempts ?? 0);
  const blocked = o.blocked === true;
  const remaining = Number(
    o.remaining ?? Math.max(0, 3 - failed),
  );
  return {
    failed_attempts: Number.isFinite(failed) ? failed : 0,
    blocked,
    remaining: Number.isFinite(remaining) ? remaining : 0,
    already_verified: o.already_verified === true,
    ok: o.ok !== false,
    error: typeof o.error === 'string' ? o.error : undefined,
  };
}

export async function getProOtpAttemptState(
  email: string,
): Promise<ProOtpAttemptState> {
  const { data, error } = await supabase.rpc('get_pro_otp_attempt_state', {
    p_email: email.toLowerCase().trim(),
  });
  if (error) {
    console.warn('[proOtpAttempts] get state failed:', error.message);
    return {
      failed_attempts: 0,
      blocked: false,
      remaining: 0,
      ok: false,
      unavailable: true,
      error: error.message,
    };
  }
  return asState(data);
}

export async function recordProOtpFailure(
  email: string,
): Promise<ProOtpAttemptState> {
  const normalized = email.toLowerCase().trim();
  try {
    const base = getApiBase().replace(/\/$/, '');
    const res = await fetch(`${base}/api/pro/record-otp-failure`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: normalized }),
    });
    const json = (await res.json().catch(() => null)) as {
      success?: boolean;
      data?: unknown;
      error?: string;
    } | null;
    if (!res.ok || !json || json.success === false) {
      console.warn(
        '[proOtpAttempts] record failure API failed:',
        json?.error ?? res.status,
      );
      return {
        failed_attempts: 0,
        blocked: false,
        remaining: 0,
        ok: false,
        unavailable: true,
        error: json?.error ?? `HTTP ${res.status}`,
      };
    }
    // Envelope: { success, data } — also accept bare data for safety.
    const payload =
      json.success !== undefined ? json.data : (json as unknown);
    return asState(payload);
  } catch (err) {
    console.warn('[proOtpAttempts] record failure threw:', err);
    return {
      failed_attempts: 0,
      blocked: false,
      remaining: 0,
      ok: false,
      unavailable: true,
      error: err instanceof Error ? err.message : 'network_error',
    };
  }
}

export async function clearProOtpFailures(email: string): Promise<void> {
  const { error } = await supabase.rpc('clear_pro_otp_failures', {
    p_email: email.toLowerCase().trim(),
  });
  if (error) {
    console.warn('[proOtpAttempts] clear failures failed:', error.message);
  }
}
